(() => {
  'use strict';
  if (customElements.get('nt-quantity-control')) return;

  const integer = value => /^\d+$/.test(String(value).trim()) && Number.isSafeInteger(Number(value)) ? Number(value) : null;
  const normalize = (value, min, step) => min + Math.ceil((Math.max(min, Math.ceil(Number(value) || min)) - min) / step) * step;

  class QuantityControl extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.abort) return;
        const form = document.getElementById(this.dataset.formId);
        const input = this.querySelector('[data-quantity-input]');
        const note = this.querySelector('[data-quantity-status]');
        const data = this.querySelector('[data-quantity-variants]');
        if (!form || !input || !data) return;
        let variants;
        try { variants = JSON.parse(data.textContent); } catch { return; }
        this.abort = new AbortController();
        const signal = this.abort.signal;
        const min = Math.max(1, parseInt(input.min, 10) || 1);
        const step = Math.max(1, parseInt(input.step, 10) || 1);
        const ruleMax = Number.isFinite(parseInt(input.max, 10)) ? parseInt(input.max, 10) : null;
        let maximum = null;
        let cartFull = false;
        const lang = String(window.Shopify?.locale || document.documentElement.lang || 'de').split('-')[0];
        const messages = {
          de: ['Die maximal verfügbare Menge dieses Artikels befindet sich bereits in deinem Warenkorb.', 'Mehr ist nicht verfügbar.'],
          nl: ['De maximaal beschikbare hoeveelheid van dit artikel zit al in uw winkelwagen.', 'Meer is niet beschikbaar.'],
          en: ['The maximum available quantity of this item is already in your cart.', 'No more stock available.']
        }[lang] || ['Die maximal verfügbare Menge dieses Artikels befindet sich bereits in deinem Warenkorb.', 'Mehr ist nicht verfügbar.'];
        const apply = fromUser => {
          const typed = integer(input.value);
          const desired = normalize(typed == null ? min : Math.max(typed, min), min, step);
          const count = maximum == null ? desired : Math.min(desired, maximum);
          input.value = String(count);
          if (note) {
            const limited = fromUser && maximum != null && desired > maximum;
            note.textContent = cartFull ? messages[0] : limited ? (window.languages?.cartQuantityLimitReached || messages[1]) : '';
            note.hidden = !note.textContent;
          }
        };
        const refresh = () => {
          const id = form.querySelector('[name="id"]')?.value;
          const info = variants[String(id)] || null;
          const managed = info?.managed && String(info.policy).toLowerCase() !== 'continue';
          const stock = info ? parseInt(info.quantity, 10) : NaN;
          const inCart = info ? parseInt(info.cartQuantity, 10) || 0 : 0;
          maximum = managed && Number.isFinite(stock) ? Math.max(0, stock - inCart) : null;
          if (ruleMax != null) maximum = maximum == null ? ruleMax : Math.min(maximum, ruleMax);
          if (maximum != null) maximum = maximum < min ? 0 : min + Math.floor((maximum - min) / step) * step;
          cartFull = Boolean(managed && Number.isFinite(stock) && stock > 0 && inCart >= stock);
          if (maximum == null) input.removeAttribute('max'); else input.max = String(maximum);
          input.disabled = maximum === 0;
          const button = form.querySelector('buy-button .product-form__add-button');
          if (button && maximum === 0 && (!button.disabled || button.dataset.quantityStockDisabled === 'true')) {
            button.disabled = true;
            button.dataset.quantityStockDisabled = 'true';
          } else if (button?.dataset.quantityStockDisabled === 'true') {
            button.disabled = false;
            delete button.dataset.quantityStockDisabled;
          }
          apply(false);
        };
        ['input', 'change', 'blur'].forEach(type => input.addEventListener(type, () => apply(true), { signal }));
        input.addEventListener('focus', () => input.select(), { signal });
        form.addEventListener('submit', event => {
          apply(true);
          if (maximum === 0) event.preventDefault();
        }, { capture: true, signal });
        document.addEventListener('variant:changed', () => queueMicrotask(() => { if (this.isConnected) refresh(); }), { signal });
        document.addEventListener('quantity:cart-state', event => {
          Object.keys(variants).forEach(id => { variants[id].cartQuantity = event.detail[id] || 0; });
          refresh();
        }, { signal });
        refresh();
        syncCart();
      });
    }
    disconnectedCallback() { this.abort?.abort(); this.abort = null; }
  }

  const styles = new Map();
  const modules = new Map();
  function loadModule(url) {
    if (!modules.has(url)) modules.set(url, import(url).catch(error => { modules.delete(url); throw error; }));
    return modules.get(url);
  }
  function loadStyles(url) {
    if (!url || styles.has(url)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    styles.set(url, link);
    link.addEventListener('error', () => { styles.delete(url); link.remove(); }, { once: true });
    document.head.appendChild(link);
  }
  class QuantityExtension extends HTMLElement {
    finishLoading() {
      this.loadingAbort?.abort();
      this.loadingAbort = null;
      if (this.loadingScope) {
        this.loadingScope.inert = this.wasInert;
        this.loadingScope.removeAttribute('aria-busy');
        this.loadingScope = null;
      }
      this.loadError?.remove();
      this.loadError = null;
    }
    connectedCallback() {
      if (this.pending || this.cleanup) return;
      const ticket = {};
      this.pending = ticket;
      queueMicrotask(async () => {
        if (!this.isConnected || this.pending !== ticket) return;
        try {
          this.finishLoading();
          const scope = (this.dataset.loadingScope && this.closest(this.dataset.loadingScope)) || this;
          this.loadingScope = scope;
          this.wasInert = scope.inert;
          scope.inert = true;
          scope.setAttribute('aria-busy', 'true');
          this.loadingAbort = new AbortController();
          const signal = this.loadingAbort.signal;
          let quantitiesDuringLoad;
          if (this.hasAttribute('data-cart-sync')) {
            document.addEventListener('quantity:cart-state', event => {
              quantitiesDuringLoad = event.detail;
            }, { signal });
            syncCart();
          }
          const stop = event => { event.preventDefault(); event.stopImmediatePropagation(); };
          scope.addEventListener('click', stop, { capture: true, signal });
          scope.addEventListener('change', stop, { capture: true, signal });
          const form = this.dataset.formId && document.getElementById(this.dataset.formId);
          if (form) {
            form.addEventListener('submit', stop, { capture: true, signal });
            form.addEventListener('click', event => {
              if (event.target.closest('buy-button, [type="submit"]')) stop(event);
            }, { capture: true, signal });
          }
          loadStyles(this.dataset.stylesheet);
          const module = await loadModule(this.dataset.module);
          if (!this.isConnected || this.pending !== ticket) return;
          this.cleanup = module.mount(this, { cartQuantities: quantitiesDuringLoad }) || (() => {});
          this.finishLoading();
        } catch (error) {
          console.warn('[quantity-extension] Unable to load component:', error);
          if (this.isConnected && this.pending === ticket) {
            const lang = String(window.Shopify?.locale || document.documentElement.lang || 'de').split('-')[0];
            this.loadError = document.createElement('p');
            this.loadError.setAttribute('role', 'alert');
            this.loadError.className = 'nt-quantity-status';
            this.loadError.textContent = {
              de: 'Die Mengenfunktion konnte nicht geladen werden. Bitte laden Sie die Seite erneut.',
              nl: 'De aantalfunctie kon niet worden geladen. Laad de pagina opnieuw.',
              en: 'The quantity controls could not be loaded. Please reload the page.'
            }[lang] || 'The quantity controls could not be loaded. Please reload the page.';
            this.loadingScope?.after(this.loadError);
          }
        } finally {
          if (this.pending === ticket) this.pending = null;
        }
      });
    }
    disconnectedCallback() {
      this.pending = null;
      this.finishLoading();
      this.cleanup?.();
      this.cleanup = null;
    }
  }

  let cartTimer;
  let cartRevision = 0;
  const syncCart = () => {
    clearTimeout(cartTimer);
    const revision = ++cartRevision;
    cartTimer = setTimeout(async () => {
      if (!document.querySelector('nt-quantity-control, nt-quantity-extension[data-cart-sync]')) return;
      try {
        const response = await fetch(((window.routes && window.routes.cartUrl) || '/cart') + '.js', { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('cart HTTP ' + response.status);
        const cart = await response.json();
        if (revision !== cartRevision) return;
        const quantities = {};
        (cart.items || []).forEach(item => { quantities[item.variant_id] = (quantities[item.variant_id] || 0) + item.quantity; });
        document.dispatchEvent(new CustomEvent('quantity:cart-state', { detail: quantities }));
      } catch (error) { console.warn('[quantity-controls] Cart sync failed:', error); }
    }, 0);
  };
  customElements.define('nt-quantity-control', QuantityControl);
  customElements.define('nt-quantity-extension', QuantityExtension);
  document.documentElement.addEventListener('product:added', syncCart);
  document.documentElement.addEventListener('cart:refresh', syncCart);
  // The theme's ordinary drawer updates emit this event without bubbling.
  document.addEventListener('cart:rerendered', syncCart, { capture: true });
  const syncOnReturn = () => { if (!document.hidden) syncCart(); };
  window.addEventListener('pageshow', event => { if (event.persisted) syncOnReturn(); });
  window.addEventListener('focus', syncOnReturn);
  document.addEventListener('visibilitychange', syncOnReturn);
})();
